import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons"

/**
 * What the base check reads, what it deliberately does not read, and what it never does.
 *
 * The StampAndDrift long-copy convention: users read a report of routing violations as a verdict
 * on their data, and this one is not that. It is a comparison of two things — the base the
 * questionnaire's routing describes, and the base the data actually carries — and where the data
 * cannot tell, it says "undetermined" rather than guessing. Saying so here, in plain sentences and
 * never behind a hover, is the difference between a report that is trusted for the right reasons
 * and one that is trusted for the wrong ones.
 *
 * @component
 */
const CoverageNotice: React.FC = () => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
    <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
      <FontAwesomeIcon icon={faCircleInfo} className="text-[11px] text-slate-400" />
      <span>What this check reads</span>
    </div>

    <p className="mb-2 text-[12px] leading-relaxed text-slate-500">
      Every respondent in the data is walked through the questionnaire&apos;s routing, one
      questionnaire variant per run. Nested <strong>IF</strong> conditions, forward{" "}
      <strong>SKIP TO</strong> jumps, <strong>TERMINATE</strong>, and <strong>FOR EACH</strong>{" "}
      loops — including the row and column slices of a grid — are all followed. The variant that
      was walked is named in the footer below, so an omitted variant is never guessed at.
    </p>

    <p className="mb-2 text-[12px] leading-relaxed text-slate-500">
      Wording variation is not routing. <code>[[if]]</code> / <code>[[else]]</code> spans change
      the text a respondent reads, not who is asked the question, so they are not evaluated here
      and never move a base.
    </p>

    <p className="mb-2 text-[12px] leading-relaxed text-slate-500">
      Where a gating answer is blank, refused or can&apos;t be read for sure, the walk can&apos;t
      tell whether the respondent was asked. They are reported as <strong>undetermined</strong>:
      never counted as off path and never cleaned. One respondent can be undetermined on some
      cells of a question and off path on others, so the two figures are never added together.
    </p>

    <p className="mb-2 text-[12px] leading-relaxed text-slate-500">
      Coverage is about tab specs, not about this check. A question whose asked condition can be
      written out is <strong>full</strong>, and its tables can be based on everyone who was asked.
      Where it can&apos;t, the question is <strong>unsupported</strong>: its tables use everyone
      who answered. It is still checked here, and its off-path answers can still be cleaned.
    </p>

    <p className="mb-2 text-[12px] leading-relaxed text-slate-500">
      A questionnaire the walk can&apos;t follow — a jump back to an earlier question, for one —
      is refused, and the reason is shown instead of a report.
    </p>

    <p className="mb-2 text-[12px] leading-relaxed text-slate-500">
      <strong>DATA</strong> fields are never offered to cleaning, whatever is asked for. Hidden
      questions are always checked and always reported, but they are not offered to cleaning in
      this version, so a hidden question&apos;s off-path answers are shown here and left in the
      file.
    </p>

    <p className="m-0 text-[12px] font-medium leading-relaxed text-slate-600">
      This report changes nothing. It reads the questionnaire and the data and writes neither; no
      answer is blanked, no file is saved, and no version is created by running it.
    </p>
  </div>
)

export default CoverageNotice
